import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import LaunchIcon from '@mui/icons-material/Launch'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid'
import { AxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import { FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  CostumerPropsType,
  CostumerType,
  EnrichedGSHOrderType,
  GHLStageType,
  UserInfoType,
  UserType,
  fetchCostumerHash,
  fetchEnrichedGSHOrders,
  fetchNewDesignHash,
} from '../../../api/appAPI'
import { saveGridStateInLocalStorage } from '../../../common/helpers'
import { CellWithTooltip } from '../../../components/Three/CabinetCatalogDialog'
import {
  EnqueueSnackbarType,
  showMessage,
} from '../../../components/common/Helpers'
import {
  CompanyType,
  addCostumer,
  actions as appActions,
  deleteCostumer,
  editCostumer,
} from '../../../redux/appReducer'
import { StateType } from '../../../redux/store'
import DefineCostumer from './DefineCostumer'
import EditCostumer from './EditCostumer'
import ViewCostumer from './ViewCostumer'

interface IProps {
  isGSH: boolean
  user: UserType | undefined
  usersInfo: UserInfoType[]
  company: CompanyType | undefined
  costumersList: CostumerType[]
  costumerGridVisibilityModel: GridColumnVisibilityModel | undefined
  costumerGridSortModel: GridSortModel | undefined
  costumerGridFilterModel: GridFilterModel | undefined

  setCostumerGridVisibilityModel(
    model: GridColumnVisibilityModel | undefined
  ): void
  setCostumerGridSortModel(model: GridSortModel | undefined): void
  setCostumerGridFilterModel(model: GridFilterModel | undefined): void
  addCostumer(
    costumerProps: CostumerPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  editCostumer(
    id: number,
    costumerProps: CostumerPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  deleteCostumer(id: number, enqueueSnackbar: EnqueueSnackbarType): void
}

const Costumers: FC<IProps> = (props) => {
  const [activeCostumerId, setActiveCostumerId] = useState(0)
  const [activeCostumer, setActiveCostumer] = useState<
    undefined | null | CostumerType
  >(props.costumersList.find((c) => c.id === activeCostumerId))

  const [viewCostumerId, setViewCostumerId] = useState<number | null>(null)
  const viewCostumer = props.costumersList.find((c) => c.id === viewCostumerId)

  const [enrichedOrders, setEnrichedOrders] = useState<EnrichedGSHOrderType[]>([])
  const [pipelineStages, setPipelineStages] = useState<GHLStageType[]>([])
  const [loadingOrders, setLoadingOrders] = useState(false)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteID, setDeleteID] = useState<null | number>(null)

  const [companyCostumerStatusList, setCompanyCostumerStatusList] = useState([])

  useEffect(() => {
    let isMounted = true
    setLoadingOrders(true)
    fetchEnrichedGSHOrders()
      .then((res) => {
        if (isMounted) {
          setEnrichedOrders(res.orders)
          setPipelineStages(res.pipelineStages)
        }
      })
      .catch((err) => {
        console.error('Error fetching GSH orders:', err)
      })
      .finally(() => {
        if (isMounted) {
          setLoadingOrders(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  const handleDeleteCostumerClick = (id: number) => {
    setDeleteID(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = () => {
    setDeleteID(null)
    setDeleteDialogOpen(false)
  }

  const handleDeleteDialogDeleteClick = () => {
    if (deleteID) props.deleteCostumer(deleteID, enqueueSnackbar)
    handleDeleteDialogClose()
  }

  useEffect(() => {
    if (activeCostumerId === -1) {
      setActiveCostumer(null)
    } else {
      setActiveCostumer(
        props.costumersList.find((h) => h.id === activeCostumerId)
      )
    }
  }, [activeCostumerId])

  useEffect(() => {
    saveGridStateInLocalStorage(
      {
        visibilityModel: props.costumerGridVisibilityModel,
        sortModel: props.costumerGridSortModel,
        filterModel: props.costumerGridFilterModel,
      },
      'costumer'
    )
  }, [
    props.costumerGridVisibilityModel,
    props.costumerGridSortModel,
    props.costumerGridFilterModel,
  ])

  useEffect(() => {
    if (props.company) {
      try {
        const options = JSON.parse(props.company.options)

        if (options.customerStatuses)
          setCompanyCostumerStatusList(options.customerStatuses.split(';'))
        else setCompanyCostumerStatusList([])
      } catch (error) {
        console.error('invalid company options - ', props.company.options)
        setCompanyCostumerStatusList([])
      }
    } else {
      setCompanyCostumerStatusList([])
    }
  }, [props.company])

  useEffect(() => {
    if (activeCostumer && props.user && props.user.role === 'admin') {
      const companyOwnerUser = props.usersInfo.find(
        (u) => u.email === activeCostumer.companyEmail
      )
      if (companyOwnerUser) {
        try {
          const options = JSON.parse(companyOwnerUser.options)
          if (options.customerStatuses) {
            setCompanyCostumerStatusList(options.customerStatuses.split(';'))
          }
        } catch (error) {
          console.error('Invalid company options - ', companyOwnerUser.options)
        }
      }
    }
  }, [activeCostumer])

  const { enqueueSnackbar } = useSnackbar()

  const handleDesignClick = async (id: number) => {
    const costumer = props.costumersList.find((costumer) => costumer.id === id)

    if (!costumer) return

    try {
      const hash = await fetchCostumerHash(costumer.email, props.isGSH ? 1 : 0)

      console.log(hash)
      window.open('/dt?h=' + hash, '_blank')
    } catch (error) {
      showMessage(enqueueSnackbar, (error as AxiosError<any>).response?.data)
    }
  }

  const handleNewDesignClick = async (id: number) => {
    const costumer = props.costumersList.find((costumer) => costumer.id === id)

    if (!costumer) return

    try {
      const data = await fetchNewDesignHash(costumer.email)
      const hash = data?.[0]?.hash

      if (hash) {
        window.open('/dt?h=' + hash, '_blank')
      } else {
        showMessage(enqueueSnackbar, 'Error creating new design')
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (error as AxiosError<any>).response?.data)
    }
  }

  return (
    <Box sx={{ height: window.innerHeight - 72 }}>
      <Button
        sx={{ m: 1 }}
        variant="contained"
        onClick={() => {
          setActiveCostumerId(-1)
        }}
      >
        Add Customer
      </Button>

      <DataGrid
        style={{ maxHeight: '100%', overflowY: 'auto' }}
        columns={
          (() => {
            const columns = [
              {
                field: 'actions',
                headerName: 'Actions',
                filterable: false,
                sortable: false,
                renderCell: (params: GridCellParams) => (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation() // don't select this row after clicking
                    }}
                  >
                    <Tooltip title="View Customer Details">
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setViewCostumerId(parseInt(params.id.toString()))
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Costumer">
                      <IconButton
                        sx={{ ml: 1.5 }}
                        edge="end"
                        onClick={() => {
                          setActiveCostumerId(parseInt(params.id.toString()))
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open last design">
                      <IconButton
                        sx={{ ml: 1.5 }}
                        edge="end"
                        onClick={() => {
                          handleDesignClick(parseInt(params.id.toString()))
                        }}
                      >
                        <LaunchIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="New design">
                      <IconButton
                        sx={{ ml: 1.5 }}
                        edge="end"
                        onClick={() => {
                          handleNewDesignClick(parseInt(params.id.toString()))
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Costumer">
                      <IconButton
                        sx={{ ml: 1.5 }}
                        edge="end"
                        onClick={() => {
                          handleDeleteCostumerClick(
                            parseInt(params.id.toString())
                          )
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
                width: 220,
              },
              { field: 'id', type: 'number', headerName: 'id', width: 100 },
              {
                field: 'date',
                headerName: 'Last Modified',
                type: 'datetime',
                minWidth: 160,
              },
              { field: 'name', headerName: 'Full Name', width: 150 },
              { field: 'email', headerName: 'Email', width: 150 },
              { field: 'status', headerName: 'Status', width: 150 },
              { field: 'phone', headerName: 'Phone', width: 150 },
              { field: 'address1', headerName: 'Address 1', width: 150 },
              { field: 'address2', headerName: 'Address 2', width: 150 },
              { field: 'zip', headerName: 'Zip Code', width: 150 },
              { field: 'city', headerName: 'City', width: 150 },
              { field: 'province', headerName: 'Province', width: 150 },
              {
                field: 'note',
                headerName: 'Note',
                width: 150,
                renderCell: (params: GridRenderCellParams) => (
                  <CellWithTooltip value={params.value?.toString() || ''} />
                ),
              },
            ]
            if (props.user && props.user.role === 'admin') {
              columns.splice(1, 0, {
                field: 'companyEmail',
                headerName: 'Company Email',
                width: 150,
              })
            }
            if (
              props.user &&
              ['admin', 'company', 'engineer'].includes(props.user.role)
            ) {
              columns.splice(1, 0, {
                field: 'creatorEmail',
                headerName: 'Created By',
                width: 150,
              })
            }
            return columns
          })() as GridColDef[]
        }
        // hideFooterPagination
        rows={props.costumersList.map((costumer) => ({
          id: costumer.id,
          date: new Date(costumer.date),
          name: `${costumer.firstName} ${costumer.lastName}`,
          email: costumer.email,
          status: costumer.status,
          phone: costumer.phone,
          address1: costumer.address1,
          address2: costumer.address2,
          zip: costumer.zip,
          city: costumer.city,
          province: costumer.province,
          creatorEmail:
            [
              {
                email: props.company?.email || '',
                name: props.company?.ownerName || '',
              },
              ...props.usersInfo,
            ].find((u) => {
              // console.log(u.email, costumer.creatorEmail);

              return u.email === costumer.creatorEmail
            })?.name || '',
          companyEmail: costumer.companyEmail,
          note: costumer.note,
        }))}
        onRowClick={(param) => {
          setViewCostumerId(parseInt(param.id.toString()))
        }}
        onRowDoubleClick={(param) => {
          setActiveCostumerId(parseInt(param.id.toString()))
        }}
        columnVisibilityModel={props.costumerGridVisibilityModel}
        onColumnVisibilityModelChange={(model) => {
          props.setCostumerGridVisibilityModel({ ...model })
        }}
        sortModel={props.costumerGridSortModel}
        onSortModelChange={(model) => {
          if (
            JSON.stringify(model) !==
            JSON.stringify(props.costumerGridSortModel)
          )
            props.setCostumerGridSortModel(model)
        }}
        filterModel={props.costumerGridFilterModel}
        onFilterModelChange={(model) => {
          if (
            JSON.stringify(model) !==
            JSON.stringify(props.costumerGridFilterModel)
          )
            props.setCostumerGridFilterModel(model)
        }}
        showCellVerticalBorder
        rowSelectionModel={activeCostumerId}
      />
      {/* </Box> */}
      <Dialog open={deleteDialogOpen}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete{' '}
            <b>
              "
              {
                props.costumersList.find((costumer) => costumer.id === deleteID)
                  ?.email
              }
              "
            </b>{' '}
            customer?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteDialogDeleteClick} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {activeCostumer === undefined ? (
        <></>
      ) : activeCostumer === null ? (
        <DefineCostumer
          statusList={companyCostumerStatusList}
          addCostumer={props.addCostumer}
          onDialogClose={() => setActiveCostumerId(0)}
        />
      ) : (
        <EditCostumer
          costumer={activeCostumer}
          statusList={companyCostumerStatusList}
          editCostumer={props.editCostumer}
          onDialogClose={() => setActiveCostumerId(0)}
        />
      )}
      {viewCostumer && (
        <ViewCostumer
          open={Boolean(viewCostumer)}
          costumer={viewCostumer}
          company={props.company}
          usersInfo={props.usersInfo}
          enrichedOrders={enrichedOrders}
          pipelineStages={pipelineStages}
          loadingOrders={loadingOrders}
          onClose={() => setViewCostumerId(null)}
          onOpenDesign={(id) => handleDesignClick(id)}
        />
      )}
    </Box>
  )
}

const mapStateToProps = (state: StateType) => ({
  isGSH: state.appReducer.isGSH,
  user: state.appReducer.user,
  usersInfo: state.appReducer.usersInfo,
  company: state.appReducer.company,
  costumersList: state.appReducer.costumersList,
  costumerGridVisibilityModel: state.appReducer.costumerGrid.visibilityModel,
  costumerGridSortModel: state.appReducer.costumerGrid.sortModel,
  costumerGridFilterModel: state.appReducer.costumerGrid.filterModel,
})

export default connect(mapStateToProps, {
  setCostumerGridVisibilityModel: appActions.setCostumerGridVisibilityModel,
  setCostumerGridSortModel: appActions.setCostumerGridSortModel,
  setCostumerGridFilterModel: appActions.setCostumerGridFilterModel,
  addCostumer,
  editCostumer,
  deleteCostumer,
})(Costumers)
